import React, { useState, useLayoutEffect, useRef, createRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  setActiveFeature,
  setHoveredFeature,
  setActiveProject,
  setSidebarIsOpen,
  scrollToTopOfSidebar,
} from "../../actions/ui";
import {
  selectActiveFeature,
  selectHoveredFeature,
  selectNonActiveFilteredProjects,
  selectActiveFilteredProjects,
  selectZoomToNsw,
  selectGeoJSON,
  selectActiveProject,
  selectSidebarIsOpen,
  selectRoundFilter,
} from "../../reducers/";
import { centerFromProject, projectShape } from "../../modules/project";
import pointInPolygon from "@turf/boolean-point-in-polygon";
import bboxPolygon from "@turf/bbox-polygon";
import turfPoint from "turf-point";
import lineString from "turf-linestring";
import bbox from "@turf/bbox";
import styles from "./map.module.scss";
import "./map.css";
import DeckGL from "@deck.gl/react";
import { FlyToInterpolator } from "@deck.gl/core";

import { GeoJsonLayer } from "@deck.gl/layers";
import ReactMapGl, { SVGOverlay, NavigationControl } from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
import "mapbox-gl/dist/mapbox-gl.css";
import "./map.scss";
import { feature as topoToGeo } from "topojson-client";
import { useIsMobile } from "../../hooks/mobile.js";
import {
  colors,
  ICON_HEIGHT,
  CIRCLE_RADIUS,
  SIDEBAR_WIDTH,
  ICON_WIDTH,
} from "../../modules/theme";
import Bowser from "bowser";
const bowser = Bowser.getParser(window.navigator.userAgent);
const browser = bowser.getBrowser();
const isIE11 =
  browser.version === "11.0" && browser.name === "Internet Explorer";

const NSW_BBOX = [138, -37.90507657099996, 152, -27.457020554999974];
const NSW_BBOX_MOBILE = [138, -37.90507657099996, 155, -27.457020554999974];

const WHITE = [255, 255, 255];
const TRANSPARENT_WHITE = [255, 255, 255, 255 * 0.2];
const TRANSPARENT = [0, 0, 0, 1];
const LABEL_WIDTH = 260;
const LABEL_HEIGHT_LINE = 16;

const Map = (props) => {
  const initialViewport = {
    width: props.width,
    height: props.height,
  };
  const {
    activeFeature,
    hoveredFeature,
    nonActiveProjects,
    activeProjects,
    isSidebarOpen,
    setActiveProject,
    setHoveredFeature,
    geometryConfig,
    activeProject,
    setSidebarIsOpen,
    scrollToTopOfSidebar,
    zoomToNsw,
    roundFilter,
  } = props;

  const isMobile = useIsMobile();

  // const mapRef = useRef();
  const mapRef = createRef();

  const PROJECT_ICON_PADDING = 60;

  const createNewViewport = React.useCallback(
    (viewState) => {
      let projectsDefineBbox = false;
      let bboxActive = activeFeature
        ? [
            activeFeature.properties.bbox.slice(0, 2),
            activeFeature.properties.bbox.slice(2, 4),
          ]
        : isMobile
        ? [NSW_BBOX_MOBILE.slice(0, 2), NSW_BBOX_MOBILE.slice(2, 4)]
        : [NSW_BBOX.slice(0, 2), NSW_BBOX.slice(2, 4)];

      const responsiveSidebarWidth = isMobile ? 24 : SIDEBAR_WIDTH;

      // get the projects in the current region
      // add each lat lon and get extends.
      if (activeFeature) {
        const projectsInRegion = nonActiveProjects.filter(
          (d) => d.region === activeFeature.properties.REGION
        );

        if (projectsInRegion.length > 1) {
          // only zoom to projects extents if there's more than 1 project
          const projectsInRegionPoints = projectsInRegion.map((d) => [
            d.longitude,
            d.latitude,
          ]);
          const projectsLine = lineString(projectsInRegionPoints);
          const projectsBbox = bbox(projectsLine);
          bboxActive = [projectsBbox.slice(0, 2), projectsBbox.slice(2, 4)];
          projectsDefineBbox = isMobile ? false : true;
        }
      }

      const leftPadding = isSidebarOpen
        ? projectsDefineBbox
          ? responsiveSidebarWidth
          : isMobile
          ? 10 + responsiveSidebarWidth
          : 10
        : projectsDefineBbox
        ? PROJECT_ICON_PADDING
        : 10;

      const paddingSettings = {
        top: projectsDefineBbox ? PROJECT_ICON_PADDING + 100 : 10,
        bottom: projectsDefineBbox ? PROJECT_ICON_PADDING : 10,
        left: leftPadding + 100,
        right: projectsDefineBbox ? PROJECT_ICON_PADDING : 10,
      };

      const newViewport = new WebMercatorViewport({
        ...initialViewport,
        ...viewState,
      }).fitBounds(bboxActive, {
        padding: isMobile
          ? { top: 0, bottom: 0, left: 0, right: 0 }
          : paddingSettings,
      });

      return newViewport;
    },
    [isSidebarOpen, activeProject, activeFeature, roundFilter, isMobile]
  );

  const initialViewState = React.useMemo(() => {
    const initialViewState = createNewViewport({});
    return initialViewState;
  }, []);
  const [viewState, setViewState] = useState(initialViewState);

  const projects = nonActiveProjects.concat(activeProjects);

  const { idSelector } = geometryConfig;

  const reset = React.useCallback(() => {
    setViewState((x) => {
      const newViewport = createNewViewport(x);
      return {
        ...x,
        latitude: newViewport.latitude,
        longitude: newViewport.longitude,
        zoom: newViewport.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  });

  useLayoutEffect(() => {
    if (!mapRef.current || !mapRef.current.getMap()) {
      return;
    }
    if (!activeFeature) {
      reset();
      return;
    }

    setViewState((viewState) => {
      const newViewState = createNewViewport(viewState);
      return {
        latitude: newViewState.latitude,
        longitude: newViewState.longitude,
        zoom: newViewState.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  }, [activeFeature, zoomToNsw, roundFilter, isMobile]);

  useLayoutEffect(() => {
    if (!mapRef.current || !mapRef.current.getMap()) {
      return;
    }
    if (!activeProject || !viewState) return;

    setViewState((viewState) => {
      const newViewState = createNewViewport(viewState);

      const hasZoomedInMoreThanRegion = newViewState.zoom < viewState.zoom;

      return {
        latitude: hasZoomedInMoreThanRegion
          ? viewState.latitude
          : newViewState.latitude,
        longitude: hasZoomedInMoreThanRegion
          ? viewState.longitude
          : newViewState.longitude,
        zoom: hasZoomedInMoreThanRegion ? viewState.zoom : newViewState.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  }, [activeProject]);

  const setActiveFeatureIfNeeded = (feature) => {
    if (!feature) {
      if (activeFeature) {
        setActiveFeature(null);
      }
      return;
    }
    if (idSelector(feature) !== idSelector(activeFeature)) {
      props.setActiveFeature(feature);
    }
  };

  return (
    <React.Fragment>
      {initialViewState && viewState && initialViewport && (
        <ReactMapGl
          style={{ position: "absolute", top: 0, left: 0 }}
          width={initialViewport.width}
          height={initialViewport.height}
          initialViewState={initialViewState}
          viewState={viewState}
          onViewportChange={(x) => {
            setViewState((currentViewState) => ({
              ...currentViewState,
              ...x,
            }));
          }}
          ref={mapRef}
          mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
          mapStyle={"mapbox://styles/smallmultiples/ckq645n0w25yh17lb4p11m1et"}
          getCursor={({ isDragging }) => {
            if (hoveredFeature) {
              return "pointer";
            }
            return isDragging ? "grabbing" : "grab";
          }}
        >
          <DeckGL
            width={initialViewport.width}
            height={initialViewport.height}
            viewState={viewState}
            controller={false}
            getCursor={({ isDragging }) => {
              if (hoveredFeature) {
                return "pointer";
              }
              return isDragging ? "grabbing" : "grab";
            }}
            layers={[
              new GeoJsonLayer({
                id: "electorates",
                data: geometryConfig.geojsonUrl,
                dataTransform: (data) => {
                  const geojson = topoToGeo(
                    data,
                    data.objects[geometryConfig.topologyObjectsName]
                  );
                  return geojson;
                },
                stroked: true,
                lineWidthUnits: "pixels",
                // Increase line width if feature is hovered or active
                getLineWidth: (d) => {
                  if (
                    idSelector(d) === idSelector(hoveredFeature) ||
                    idSelector(d) === idSelector(activeFeature)
                  ) {
                    return 3;
                  } else {
                    return 1;
                  }
                },
                lineWidthMinPixels: 0.5,
                getFillColor: (d) => {
                  if (
                    idSelector(d) === idSelector(hoveredFeature) ||
                    idSelector(d) === idSelector(activeFeature)
                  ) {
                    return TRANSPARENT;
                  } else {
                    return TRANSPARENT_WHITE;
                  }
                },
                highlightColor: TRANSPARENT,
                getLineColor: WHITE,
                pickable: true,
                updateTriggers: {
                  // Make sure the map updates when a feature is hovered/active
                  // So we can redraw line widths and change transparency
                  // But not on IE11 as it doesn't work
                  getFillColor: isIE11 ? [] : [activeFeature, hoveredFeature],
                  getLineWidth: [activeFeature, hoveredFeature],
                },
                autoHighlight: true,
                // activeFeature is set when you click or hover
                onClick: (d) => {
                  setSidebarIsOpen(true);
                  setActiveFeatureIfNeeded(d.object);
                  setActiveProject(null);
                },
                onHover: ({ object, x, y }) => {
                  setHoveredFeature(object);
                },
              }),
            ]}
          />
          <SVGOverlay
            redraw={({ project }) => {
              const getRegionFromCircle = (circle) => {
                if (!props.geojson) return;
                return props.geojson.features.find((feature) => {
                  const featureBboxPoly = bboxPolygon(feature.properties.bbox);
                  const point = turfPoint(centerFromProject(circle));

                  if (!pointInPolygon(point, featureBboxPoly)) {
                    return false;
                  }
                  return pointInPolygon(point, feature);
                });
              };

              return projects
                .filter((d) =>
                  roundFilter
                    ? roundFilter.split(",").includes(d.round.toString())
                    : true
                )
                .sort((a, b) => {
                  if (activeProject) {
                    if (a.project === activeProject) {
                      return 1;
                    } else if (b.project === activeProject) {
                      return -1;
                    }
                    return 0;
                  }
                  return 0;
                })
                .map((d, i) => {
                  const [x, y] = project(centerFromProject(d));
                  const region = activeFeature
                    ? idSelector(activeFeature)
                    : "no region";

                  const LABEL_OFFSET_X = 10;

                  // 24 is the max zoom. Default is around 7ish (1x), max out at 12ish (3x)
                  const h = isMobile ? ICON_HEIGHT * 0.8 : ICON_HEIGHT;
                  const w = isMobile ? ICON_WIDTH * 0.8 : ICON_WIDTH;
                  const iconHeight = Math.max(
                    30,
                    Math.min(120, scale(viewState.zoom, 7, 12, 1, 4) * h)
                  );
                  const iconWidth = Math.max(
                    30,
                    Math.min(120, scale(viewState.zoom, 7, 12, 1, 4) * w)
                  );

                  const labelLines = d.label.split("\\n");
                  const alignmentTransform = `translate(-${
                    LABEL_WIDTH + LABEL_OFFSET_X * 2 + ICON_WIDTH * 2
                  }px,0px)`;
                  const labelsOnLines = labelLines.map((ll, j) => {
                    return (
                      <text
                        key={j + ll}
                        dx={x + ICON_WIDTH + LABEL_OFFSET_X + 10}
                        dy={y - ICON_HEIGHT / 2 + 20 + LABEL_HEIGHT_LINE * j}
                        className={styles.labelProject}
                        style={
                          d.label_alignment.indexOf("left") >= 0
                            ? { transform: alignmentTransform }
                            : {}
                        }
                      >
                        {ll}
                      </text>
                    );
                  });
                  const labelHeight =
                    40 +
                    LABEL_HEIGHT_LINE * (labelLines.length + 1) +
                    18 +
                    LABEL_HEIGHT_LINE;

                  const label = (
                    <g
                      className={styles.label}
                      style={{
                        zIndex: 1000,
                        pointerEvents: "all",
                        cursor: "pointer",
                      }}
                    >
                      {d.label_alignment === "right" && (
                        <path
                          d="M0 0H18.8841V20L0 0Z"
                          fill="#fff"
                          style={{
                            transform: `translate(${
                              x + ICON_WIDTH + LABEL_OFFSET_X - 18
                            }px,${y - ICON_HEIGHT / 2}px)`,
                          }}
                        />
                      )}
                      {d.label_alignment === "left" && (
                        <path
                          d="M19 0H0.115879V20L19 0Z"
                          fill="#fff"
                          style={{
                            transform: `translate(${
                              x - ICON_WIDTH - LABEL_OFFSET_X - 1
                            }px,${y - ICON_HEIGHT / 2}px)`,
                          }}
                        />
                      )}
                      {d.label_alignment === "top-right" && (
                        <path
                          d="M0 20H18.8841V0L0 20Z"
                          fill="#fff"
                          style={{
                            transform: `translate(${
                              x + ICON_WIDTH + LABEL_OFFSET_X - 18
                            }px,${y - ICON_HEIGHT / 2 - 20}px)`,
                          }}
                        />
                      )}
                      {d.label_alignment === "top-left" && (
                        <path
                          d="M19 20H0.115879V0L19 20Z"
                          fill="#fff"
                          style={{
                            transform: `translate(${
                              x - ICON_WIDTH - LABEL_OFFSET_X - 1
                            }px,${y - ICON_HEIGHT / 2 - 20}px)`,
                          }}
                        />
                      )}

                      <rect
                        x={x + ICON_WIDTH + LABEL_OFFSET_X}
                        y={
                          d.label_alignment.indexOf("top") >= 0
                            ? y - ICON_HEIGHT / 2 - labelHeight
                            : y - ICON_HEIGHT / 2
                        }
                        width={LABEL_WIDTH}
                        height={labelHeight}
                        fill="#fff"
                        style={
                          d.label_alignment.indexOf("left") >= 0
                            ? { transform: alignmentTransform }
                            : {}
                        }
                      />
                      <g
                        style={
                          d.label_alignment.indexOf("top") >= 0
                            ? { transform: `translate(0,-${labelHeight}px)` }
                            : {}
                        }
                      >
                        {labelsOnLines}
                        <text
                          dx={x + ICON_WIDTH + LABEL_OFFSET_X + 10}
                          dy={
                            y -
                            ICON_HEIGHT / 2 +
                            20 +
                            LABEL_HEIGHT_LINE * labelLines.length +
                            10
                          }
                          className={styles.labelLocation}
                          fill={colors.linkGreen}
                          style={
                            d.label_alignment.indexOf("left") >= 0
                              ? { transform: alignmentTransform }
                              : {}
                          }
                        >
                          {"in " + d.location}
                        </text>
                        <text
                          dx={x + ICON_WIDTH + LABEL_OFFSET_X + 10}
                          dy={
                            y -
                            ICON_HEIGHT / 2 +
                            20 +
                            LABEL_HEIGHT_LINE * labelLines.length +
                            10 +
                            LABEL_HEIGHT_LINE
                          }
                          className={styles.labelOrganisation}
                          fill={colors.linkGreen}
                          style={
                            d.label_alignment.indexOf("left") >= 0
                              ? { transform: alignmentTransform }
                              : {}
                          }
                        >
                          {d.label_organisation}
                        </text>
                        <text
                          dx={x + ICON_WIDTH + LABEL_OFFSET_X + 10}
                          dy={
                            y -
                            ICON_HEIGHT / 2 +
                            20 +
                            LABEL_HEIGHT_LINE * labelLines.length +
                            10 +
                            LABEL_HEIGHT_LINE * 2
                          }
                          className={styles.labelOrganisation}
                          fill={colors.linkGreen}
                          style={
                            d.label_alignment.indexOf("left") >= 0
                              ? { transform: alignmentTransform }
                              : {}
                          }
                        >
                          {"Ongoing jobs to be created: " +
                            d.fte_ongoing_jobs_to_be_created}
                        </text>
                        <text
                          dx={x + ICON_WIDTH + LABEL_OFFSET_X + 10}
                          dy={
                            y -
                            ICON_HEIGHT / 2 +
                            20 +
                            LABEL_HEIGHT_LINE * labelLines.length +
                            10 +
                            LABEL_HEIGHT_LINE * 3
                          }
                          className={styles.labelRound}
                          fill={colors.linkGreen}
                          style={
                            d.label_alignment.indexOf("left") >= 0
                              ? { transform: alignmentTransform }
                              : {}
                          }
                        >
                          {"Round: " + d.round}
                        </text>
                      </g>
                    </g>
                  );

                  if (
                    activeFeature &&
                    d.region === activeFeature.properties.REGION &&
                    d.image
                  ) {
                    return (
                      <g
                        className={styles.markerContainer}
                        onClick={(e) => {
                          setActiveProject(d.project);
                          if (!isSidebarOpen) {
                            setSidebarIsOpen(true);
                            setActiveFeatureIfNeeded(d.object);
                          }
                          scrollToTopOfSidebar(Date.now());
                          if (activeProject === d.project) {
                            // close if open.
                            setActiveProject(null);
                          }
                        }}
                      >
                        <image
                          key={d.project + i}
                          xlinkHref={
                            "data:image/svg+xml," + encodeURIComponent(d.image)
                          }
                          // height={iconHeight}
                          width={
                            d.project ===
                            "The InQuik International Innovation Centre (IIIC)"
                              ? iconWidth * 0.8
                              : iconWidth
                          }
                          maxheight={iconHeight}
                          maxwidth={iconWidth}
                          x={x}
                          y={y}
                          data-key={"circle" + region + d.project + i}
                          style={{
                            transform: `translate(-${
                              iconWidth / 2
                            }px,-${iconHeight}px)`,
                            zIndex: 999,
                            pointerEvents: "all",
                            cursor: "pointer",
                          }}
                        />
                        {activeProject === d.project && label}
                      </g>
                    );
                  }

                  const debugging = false;
                  // if we're debugging, or there's no icon
                  if (
                    (activeFeature &&
                      d.region === activeFeature.properties.REGION &&
                      !d.image) ||
                    debugging
                  ) {
                    return (
                      <g className={styles.markerContainer}>
                        <circle
                          style={{ pointerEvents: "all", cursor: "pointer" }}
                          onClick={(e) => {
                            const newActiveRegion = getRegionFromCircle(d);
                            setSidebarIsOpen(true);
                            setActiveFeatureIfNeeded(newActiveRegion);
                            setActiveProject(d.project);
                            scrollToTopOfSidebar(Date.now());
                          }}
                          key={"circle" + region + d.project + i}
                          data-key={"circle" + region + d.project + i}
                          cx={x}
                          cy={y}
                          r={CIRCLE_RADIUS}
                          fill="#555"
                          strokeWidth={0}
                          opacity={0.8}
                        />
                        {activeProject === d.project && label}
                      </g>
                    );
                  }
                });
            }}
          />

          <div className={styles.navigationControlBtns}>
            <NavigationControl
              onViewportChange={(newViewState) => {
                setViewState((x) => ({
                  ...x,
                  zoom: newViewState.zoom,
                  latitude: newViewState.latitude,
                  longitude: newViewState.longitude,
                  transitionInterpolator: new FlyToInterpolator(),
                  transitionDuration: 200,
                }));
              }}
              showCompass={false}
              style={{ width: "40px" }}
            />
          </div>
        </ReactMapGl>
      )}
    </React.Fragment>
  );
};

const scale = (number, inMin, inMax, outMin, outMax) => {
  return ((number - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
};

const featureShape = {
  type: PropTypes.string.isRequired,
  geometry: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
};
Map.propTypes = {
  activeProjects: PropTypes.arrayOf(PropTypes.shape(projectShape)),
  activeProject: PropTypes.shape(projectShape),
  nonActiveProjects: PropTypes.arrayOf(PropTypes.shape(projectShape)),
  activeFeature: PropTypes.shape(featureShape),
  hoveredFeature: PropTypes.shape(featureShape),
  setActiveFeature: PropTypes.func.isRequired,
  setHoveredFeature: PropTypes.func.isRequired,
  setActiveProject: PropTypes.func.isRequired,
  setSidebarIsOpen: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  geometryConfig: PropTypes.object.isRequired,
  zoomToNsw: PropTypes.number.isRequired,
  geojson: PropTypes.object,
  scrollToTopOfSidebar: PropTypes.func.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired,
  roundFilter: PropTypes.string,
};

const mapStateToProps = (state) => ({
  activeFeature: selectActiveFeature(state),
  hoveredFeature: selectHoveredFeature(state),
  nonActiveProjects: selectNonActiveFilteredProjects(state),
  activeProjects: selectActiveFilteredProjects(state),
  zoomToNsw: selectZoomToNsw(state),
  geojson: selectGeoJSON(state),
  activeProject: selectActiveProject(state),
  isSidebarOpen: selectSidebarIsOpen(state),
  roundFilter: selectRoundFilter(state),
});
const mapDispatchToProps = {
  setActiveFeature,
  setHoveredFeature,
  setActiveProject,
  setSidebarIsOpen,
  scrollToTopOfSidebar,
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js